import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  etiquetasService,
  etiquetasGeradasService,
  etiquetasModelosService,
  produtosEtiquetasService,
  unidadesMedidaService,
} from '../../services';
import {
  LoteEtiqueta,
  TamanhoEtiqueta,
  CodeMode,
  EtiquetaModelo,
  UnidadeMedida,
  EstoqueProduto,
} from '../../types';
import { GerarEtiquetaRequest } from '../../types/etiquetas';
import { EtiquetaPreview, EtiquetaPreviewHandle } from '../../components/etiquetas/EtiquetaPreview';
import type { EtiquetaPreviewProps } from '../../components/etiquetas/EtiquetaPreview';

type EtiquetaFormData = {
  codigo_material: string;
  descricao: string;
  quantidade: number;
  unidade: string;
  lote: string;
  numero_pallet: number;
  codigo_tetra: string;
  codigo_fornecedor: string;
  data: string;
  modelo: number | null;
  tamanho_etiqueta: TamanhoEtiqueta;
  code_mode: CodeMode;
  unidade_id: string;
};

type EtiquetaPreviewDados = EtiquetaPreviewProps['dados'];

const initialFormData: EtiquetaFormData = {
  codigo_material: '',
  descricao: '',
  quantidade: 1,
  unidade: 'PC',
  lote: '',
  numero_pallet: 1,
  codigo_tetra: '',
  codigo_fornecedor: '',
  data: '',
  modelo: null,
  tamanho_etiqueta: '100x50',
  code_mode: 'barcode',
  unidade_id: 'PC',
};
const UNIDADES_PADRAO = [
  { codigo: 'PC', descricao: 'Peça' },
  { codigo: 'KG', descricao: 'Quilograma' },
  { codigo: 'M', descricao: 'Metro' },
  { codigo: 'M2', descricao: 'Metro Quadrado' },
  { codigo: 'M3', descricao: 'Metro Cúbico' },
  { codigo: 'UN', descricao: 'Unidade' },
];

export const EtiquetasFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [lotes, setLotes] = useState<LoteEtiqueta[]>([]);
  const [modelos, setModelos] = useState<EtiquetaModelo[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState<EstoqueProduto | null>(null);
  const [modeloSelecionado, setModeloSelecionado] = useState<EtiquetaModelo | null>(null);
  const [loteSelecionadoId, setLoteSelecionadoId] = useState<number | null>(null);
  const [pendingProdutoId, setPendingProdutoId] = useState<number | null>(null);
  const [pendingLoteId, setPendingLoteId] = useState<number | null>(null);
  const previewRef = useRef<EtiquetaPreviewHandle | null>(null);
  const [unidadesMedida, setUnidadesMedida] = useState<UnidadeMedida[]>([]);
  const [temLoteFilter, setTemLoteFilter] = useState(false);
  const [temEstoqueFilter, setTemEstoqueFilter] = useState(false);
  const [tetrapakFilter, setTetrapakFilter] = useState(false);
  const [usarModeloTetrapak, setUsarModeloTetrapak] = useState(false);
  const [produtosEtiquetas, setProdutosEtiquetas] = useState<EstoqueProduto[]>([]);
  const [loadingEstoqueProdutos, setLoadingEstoqueProdutos] = useState(false);

  const [formData, setFormData] = useState<EtiquetaFormData>(initialFormData);
  const [previewData, setPreviewData] = useState<EtiquetaPreviewDados | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [, setLoadingEtiqueta] = useState(false);
  const params = useParams<{ id?: string }>();
  const etiquetaId = params.id ? Number(params.id) : null;
  const shouldUseTetrapakModel = usarModeloTetrapak;
  const resolveProdutoIdentificador = useCallback(() => {
    return String(
      produtoSelecionado?.produto_codigo ??
        produtoSelecionado?.produto ??
        produtoSelecionado?.produto_id ??
        produtoSelecionado?.id ??
        pendingProdutoId ??
        '',
    );
  }, [produtoSelecionado, pendingProdutoId]);
  const padLeft = (value: string, size: number) => value.padStart(size, '0');

  const getUnitCode = (lote: string, palletNumber: number) => {
    const loteStr = lote || '';
    const pallet = padLeft(String(palletNumber || 0), 4);
    const lotePart = loteStr;
    return `${lotePart}${pallet}`;
  };

  const getQuantCode = (lote: string, palletNumber: number, codigoMaterial: string, unidade: string, quantidade: number) => {
    const lotePadded = padLeft(String(lote || ''), 10);
    const pallet = padLeft(String(palletNumber || 0), 4);
    const codMat = String(codigoMaterial || '').slice(0, -1) || '';
    const unidadeValue = unidade || 'PC';
    const qtd = padLeft(String(quantidade || 0), 6);
    return `${lotePadded}${pallet}${codMat}${unidadeValue}${qtd}`;
  };

  const buildPreviewDados = (data: EtiquetaFormData, numeroPallet: number): EtiquetaPreviewDados => ({
    codigo_material: data.codigo_material,
    descricao: data.descricao,
    quantidade: data.quantidade,
    unidade: data.unidade,
    lote: data.lote,
    numero_pallet: numeroPallet,
    codigo_tetra: data.codigo_tetra,
    codigo_fornecedor: data.codigo_fornecedor,
    data: data.data,
    tamanho_etiqueta: data.tamanho_etiqueta,
    code_mode: data.code_mode,
  });

  const handleVisualizar = () => {
    setError(null);
    setSuccess(false);

    if (!modeloSelecionado) {
      setError('Selecione um modelo de etiqueta.');
      return;
    }
    const produtoIdentifier = resolveProdutoIdentificador();
    if (shouldUseTetrapakModel && !formData.lote) {
      setError('Selecione um lote válido para gerar a etiqueta.');
      return;
    }

    const loteVisualizar = shouldUseTetrapakModel
      ? formData.lote || produtoIdentifier
      : produtoIdentifier || formData.lote;
    if (!loteVisualizar) {
      setError('Informe um lote ou identificador válido para gerar a etiqueta.');
      return;
    }
    const quantidadeValor = Number(formData.quantidade);
    if (quantidadeValor <= 0) {
      setError('Informe uma quantidade válida.');
      return;
    }

    const numeroPallet = Number(formData.numero_pallet || 1);
    setPreviewReady(false);
    setPreviewLoading(true);
    const codigoMaterialPreview = shouldUseTetrapakModel
      ? formData.lote || produtoIdentifier
      : produtoSelecionado?.produto_codigo ?? formData.codigo_material ?? produtoIdentifier;
    setPreviewData(
      buildPreviewDados(
        { ...formData, codigo_material: codigoMaterialPreview, lote: loteVisualizar },
        numeroPallet,
      ),
    );
  };

  // Buscar modelos disponíveis
  useEffect(() => {
    const fetchModelos = async () => {
      setLoading(true);
      try {
        const modelosResponse = await etiquetasModelosService.list({ ativo: true });
        setModelos(modelosResponse.results || []);
      } catch (err) {
        console.error('Erro ao buscar modelos de etiquetas:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchModelos();
  }, []);

  const tetrapakFilterRef = useRef(tetrapakFilter);

  useEffect(() => {
    tetrapakFilterRef.current = tetrapakFilter;
  }, [tetrapakFilter]);

  useEffect(() => {
    let cancelled = false;

    const loadEstoqueProdutos = async () => {
      setLoadingEstoqueProdutos(true);
      try {
        const params: Record<string, string | number> = { page_size: 200 };
        if (temLoteFilter) {
          params.tem_lote = 'sim';
        }
        if (temEstoqueFilter) {
          params.tem_estoque = 'sim';
        }
        if (tetrapakFilterRef.current) {
          params.tetrapak = 'sim';
        }
        const response = await produtosEtiquetasService.list(params);
        if (!cancelled) {
          const items = Array.isArray(response)
            ? response
            : response.results ?? [];
          const normalized = items.map((item) => ({
            ...item,
            produto: item.produto ?? item.produto_id ?? item.produto,
          }));
          setProdutosEtiquetas(normalized);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao buscar produtos em estoque:', err);
          setProdutosEtiquetas([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingEstoqueProdutos(false);
        }
      }
    };

    loadEstoqueProdutos();

    return () => {
      cancelled = true;
    };
  }, [temLoteFilter, temEstoqueFilter]);

  useEffect(() => {
    const fetchUnidades = async () => {
      try {
        const response = await unidadesMedidaService.list();
        const list = Array.isArray(response)
          ? response
          : response.results || response;
        setUnidadesMedida(list);
      } catch (err) {
        console.error('Erro ao buscar unidades de medida:', err);
      }
    };
    fetchUnidades();
  }, []);

  useEffect(() => {
    if (!unidadesMedida.length) {
      return;
    }
    if (unidadesMedida.some((u) => String(u.id) === formData.unidade_id)) {
      return;
    }
    const fallback = unidadesMedida[0];
    setFormData((prev) => ({
      ...prev,
      unidade_id: String(fallback.id),
      unidade: fallback.codigo,
    }));
  }, [unidadesMedida, formData.unidade_id]);

  useEffect(() => {
    if (!previewData) {
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewReady(false);
    previewRef.current
      ?.generatePreview()
      .then(() => {
        if (!cancelled) {
          setPreviewReady(true);
        }
      })
      .catch((err) => {
        console.error('Erro ao gerar preview:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [previewData]);

  useEffect(() => {
    if (!etiquetaId) {
      return;
    }
    let cancelled = false;
    const fetchEtiqueta = async () => {
      setLoadingEtiqueta(true);
      setError(null);
      try {
        const etiqueta = await etiquetasGeradasService.retrieve(etiquetaId);
        if (cancelled) {
          return;
        }
        setFormData((prev) => {
          const modeloValue = etiqueta.modelo ?? etiqueta.modelo_id ?? prev.modelo;
          const quantidadeValue =
            etiqueta.quantidade ?? etiqueta.quantidade_etiquetas ?? prev.quantidade;
          const unidadeIdValue = etiqueta.unidade_id ?? prev.unidade_id;
          const unidadeCodigo = etiqueta.unidade ?? etiqueta.unidade_codigo;

          return {
            ...prev,
            codigo_material: etiqueta.codigo_material ?? prev.codigo_material,
            descricao: etiqueta.descricao ?? prev.descricao,
            quantidade: quantidadeValue,
            unidade_id:
              unidadeIdValue !== undefined && unidadeIdValue !== null
                ? String(unidadeIdValue)
                : prev.unidade_id,
            unidade: unidadeCodigo ?? prev.unidade,
            lote: etiqueta.lote ?? prev.lote,
            numero_pallet: etiqueta.numero_pallet ?? prev.numero_pallet,
            codigo_tetra: etiqueta.codigo_tetra ?? prev.codigo_tetra,
            codigo_fornecedor: etiqueta.codigo_fornecedor ?? prev.codigo_fornecedor,
            data: etiqueta.data ?? prev.data ?? '',
            modelo: modeloValue,
          };
        });
        if (etiqueta.lote_id) {
          setLoteSelecionadoId(etiqueta.lote_id);
        }
        const parsedProdutoId =
          etiqueta.produto_id !== undefined && etiqueta.produto_id !== null
            ? Number(etiqueta.produto_id)
            : null;
        setPendingProdutoId(
          parsedProdutoId !== null && Number.isFinite(parsedProdutoId) ? parsedProdutoId : null,
        );
        setPendingLoteId(etiqueta.lote_id ?? null);
        const tetrapakValue = Boolean(etiqueta.tetrapak);
        setUsarModeloTetrapak(tetrapakValue);
        setTetrapakFilter(tetrapakValue);
        setUsarModeloTetrapak(Boolean(etiqueta.tetrapak));
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar etiqueta:', err);
          setError('Não foi possível carregar os dados da etiqueta.');
        }
      } finally {
        if (!cancelled) {
          setLoadingEtiqueta(false);
        }
      }
    };

    fetchEtiqueta();

    return () => {
      cancelled = true;
    };
  }, [etiquetaId]);

  const estoqueProductOptions = useMemo(() => {
    const seen = new Map<number, EstoqueProduto>();
    produtosEtiquetas.forEach((item) => {
      const key = item.produto ?? item.produto_id ?? NaN;
      if (Number.isFinite(key) && !seen.has(key)) {
        seen.set(key, { ...item, produto: key });
      }
    });

    return Array.from(seen.values()).map((item) => {
      const quantity = item.quantidade_disponivel ?? 0;
      const labelParts = [
        item.produto_codigo ?? String(item.produto),
        item.produto_descricao ?? '',
      ]
        .filter(Boolean)
        .join(' - ');
      const postfix = `${quantity.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} un`;
      const tipo = item.tipo_estoque ? ` (${item.tipo_estoque})` : '';
      const hints = [];
      if (item.tem_lote === false) {
        hints.push('sem lote');
      }
      if (item.tem_estoque === false) {
        hints.push('sem estoque');
      }
      if (item.tetrapak) {
        hints.push('tetrapak');
      }
      const hintText = hints.length ? ` • ${hints.join(' / ')}` : '';
      return {
        value: item.produto,
        label: `${labelParts} — ${postfix}${tipo}${hintText}`,
        tem_lote: item.tem_lote,
        tem_estoque: item.tem_estoque,
      };
    });
  }, [produtosEtiquetas]);

  useEffect(() => {
    if (!formData.modelo || !modelos.length) {
      return;
    }
    const selected = modelos.find((m) => m.id === formData.modelo);
    if (selected) {
      setModeloSelecionado((current) =>
        current?.id === selected.id ? current : selected,
      );
    }
  }, [formData.modelo, modelos]);

  const selectProduto = useCallback(
    (
      produto: EstoqueProduto,
      options?: { keepLote?: boolean; loteId?: number | null },
    ) => {
      const { keepLote = false, loteId = null } = options ?? {};
      const availableLotes = produto.lotes ?? [];
      const fallbackLoteId =
        keepLote && loteId != null
          ? loteId
          : availableLotes.length
          ? availableLotes[0].lote_id
          : null;
      const selectedLote =
        fallbackLoteId != null
          ? availableLotes.find((lote) => lote.lote_id === fallbackLoteId)
          : undefined;
      const loteCodigoSelected = selectedLote?.codigo_numerico ?? selectedLote?.codigo ?? '';
      const hasLotOptions = availableLotes.length > 0;
      const produtoIdentifier = String(
        produto.produto_codigo ?? produto.produto ?? produto.produto_id ?? produto.id ?? '0',
      );

      setProdutoSelecionado(produto);
      setLotes(availableLotes);
      setLoteSelecionadoId(fallbackLoteId);
      setFormData((prev) => ({
        ...prev,
        codigo_material:
          hasLotOptions
            ? produto.produto_codigo ?? prev.codigo_material
            : produtoIdentifier,
        descricao: produto.produto_descricao ?? prev.descricao,
        lote: keepLote
          ? prev.lote
          : hasLotOptions
          ? loteCodigoSelected
          : String(produto.produto ?? produto.produto_id ?? produto.id ?? '0'),
      }));
    },
    [],
  );

  // Buscar lotes quando um produto é selecionado
  const handleProdutoChange = (produtoId: string) => {
    const id = Number(produtoId);
    if (!Number.isFinite(id)) {
      return;
    }
    const produto = produtosEtiquetas.find((item) => item.produto === id);
    if (!produto) {
      return;
    }

    selectProduto(produto);
  };

  useEffect(() => {
    if (pendingProdutoId === null || !produtosEtiquetas.length) {
      return;
    }
    if (produtoSelecionado?.produto === pendingProdutoId) {
      setPendingProdutoId(null);
      setPendingLoteId(null);
      return;
    }
    const match = produtosEtiquetas.find((item) => item.produto === pendingProdutoId);
    if (!match) {
      return;
    }
    selectProduto(match, {
      keepLote: pendingLoteId != null,
      loteId: pendingLoteId,
    });
    setPendingProdutoId(null);
    setPendingLoteId(null);
  }, [pendingProdutoId, pendingLoteId, produtosEtiquetas, produtoSelecionado, selectProduto]);

  const handleLoteChange = (loteIdValue: string) => {
    const loteId = loteIdValue ? Number(loteIdValue) : null;
    setLoteSelecionadoId(loteId);
    setError(null);

    if (!loteId) {
      setFormData((prev) => ({ ...prev, lote: '' }));
      return;
    }

    const lote = lotes.find((item) => item.lote_id === loteId);
    if (!lote) {
      setFormData((prev) => ({ ...prev, lote: '' }));
      setError('Lote selecionado inválido.');
      return;
    }

    const codigoNumerico = lote.codigo_numerico ?? lote.codigo ?? '';
    if (!codigoNumerico) {
      setFormData((prev) => ({ ...prev, lote: '' }));
      setError('Lote selecionado sem código numérico para etiqueta.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      lote: codigoNumerico,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const isEditing = Boolean(etiquetaId);

    const modeloId = formData.modelo;
    if (!modeloId) {
      setError('Selecione um modelo de etiqueta.');
      return;
    }
    if (shouldUseTetrapakModel && !formData.lote) {
      setError('Selecione um lote válido para gerar a etiqueta.');
      return;
    }
    const produtoIdentifier = resolveProdutoIdentificador();
    if (!produtoIdentifier && !formData.lote) {
      setError('Informe um lote ou identificador válido para gerar a etiqueta.');
      return;
    }
    const quantidadeValor = Number(formData.quantidade);
    if (quantidadeValor <= 0) {
      setError('Informe uma quantidade válida.');
      return;
    }

    setLoading(true);
    try {
      const numeroPallet = Number(formData.numero_pallet || 1);
      const barcodeSource = shouldUseTetrapakModel
        ? formData.lote || produtoIdentifier
        : produtoIdentifier || formData.lote;
      const codigoMaterialForCodes = shouldUseTetrapakModel
        ? formData.lote || produtoIdentifier
        : produtoSelecionado?.produto_codigo ?? formData.codigo_material ?? produtoIdentifier;
      const unitCode = getUnitCode(
        barcodeSource,
        numeroPallet,
      );
      const quantCode = getQuantCode(
        barcodeSource,
        numeroPallet,
        codigoMaterialForCodes,
        formData.unidade,
        quantidadeValor,
      );

      const loteValue = loteSelecionadoId ? String(loteSelecionadoId) : '';
      const tetrapakFlag = shouldUseTetrapakModel;
      const payload: GerarEtiquetaRequest = {
        modelo_id: modeloId,
        lote_id: loteSelecionadoId ?? undefined,
        quantidade_etiquetas: quantidadeValor,
        unidade_id: formData.unidade_id,
        lote: loteValue,
        numero_pallet: numeroPallet,
        code_barra_vertical: unitCode,
        code_barra_horizontal: quantCode,
        code_mode: formData.code_mode,
        logo_path: null,
        codigo_tetra: formData.codigo_tetra || undefined,
        codigo_fornecedor: formData.codigo_fornecedor || undefined,
        data: formData.data || undefined,
        produto_id: produtoSelecionado?.produto,
        tetrapak: tetrapakFlag,
      };

      const sanitizedPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, value]) => value !== undefined),
      ) as GerarEtiquetaRequest;
      console.log('Payload Etiqueta:', sanitizedPayload);
      const request = isEditing
        ? etiquetasService.update(etiquetaId!, sanitizedPayload)
        : etiquetasService.gerar(sanitizedPayload);
      await request;
      setSuccess(true);
      setTimeout(() => navigate('/admin/producao/etiquetas'), 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao gerar etiqueta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0">
        <div className="card-body pb-6">
          <div className="d-flex flex-column gap-4">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="mb-1 mb-lg-0">Gerar Etiqueta</h5>
              <span className="badge bg-warning text-dark">Editando</span>
            </div>
            <p className="text-muted mb-0">
              Gere novas etiquetas a partir de modelos já existentes e lote selecionado.
            </p>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && <Alert variant="success">Etiqueta salva com sucesso!</Alert>}

            <Form onSubmit={handleSave}>
              <div className="d-flex flex-wrap gap-3 align-items-end">
                <div className="flex-fill">
                  <Form.Label className="fw-bold mb-2 me-2">Modelo de Etiqueta *</Form.Label>
                  <Form.Select
                    required
                    value={formData.modelo || ''}
                    onChange={(e) => {
                      const modeloId = e.target.value ? Number(e.target.value) : null;
                      const modelo = modelos.find((m) => m.id === modeloId);
                      setModeloSelecionado(modelo || null);
                      setFormData((prev) => ({
                        ...prev,
                        modelo: modeloId,
                        tamanho_etiqueta: modelo
                          ? `${modelo.largura_mm}x${modelo.altura_mm}` as TamanhoEtiqueta
                          : prev.tamanho_etiqueta,
                      }));
                    }}
                  >
                    <option value="">Selecione um modelo</option>
                    {modelos.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nome} - {m.largura_mm}x{m.altura_mm}mm
                        {m.familia && ` (${m.familia})`}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex gap-3 flex-wrap align-items-center pb-2">
                  <Form.Check
                    inline
                    type="checkbox"
                    id="tem-lote-checkbox"
                    label="Lote"
                    checked={temLoteFilter}
                    onChange={(event) => setTemLoteFilter(event.target.checked)}
                  />
                  <Form.Check
                    inline
                    type="checkbox"
                    id="tem-estoque-checkbox"
                    label="Estoque"
                    checked={temEstoqueFilter}
                    onChange={(event) => setTemEstoqueFilter(event.target.checked)}
                  />
                <Form.Check
                  inline
                  type="checkbox"
                  id="tetrapak-checkbox"
                  label="Tetrapak"
                  checked={tetrapakFilter}
                  onChange={(event) => {
                    const { checked } = event.target;
                    setTetrapakFilter(checked);
                    setUsarModeloTetrapak(checked);
                  }}
                />
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <Form.Label className="fw-bold">Produto *</Form.Label>
                    <Form.Select
                      value={produtoSelecionado?.produto ?? ''}
                      onChange={(e) => handleProdutoChange(e.target.value)}
                      disabled={!modeloSelecionado || loadingEstoqueProdutos}
                    >
                      <option value="" disabled>
                        {modeloSelecionado ? 'Selecione um produto' : 'Selecione um modelo primeiro'}
                      </option>
                      {estoqueProductOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingEstoqueProdutos && (
                      <div className="mt-2 text-muted small d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        Carregando produtos...
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <Form.Label>Lote</Form.Label>
                    <Form.Select
                      value={loteSelecionadoId ?? ''}
                      onChange={(e) => handleLoteChange(e.target.value)}
                      disabled={!produtoSelecionado}
                    >
                      <option value="">Selecione um lote</option>
                      {lotes.map((lote) => {
                        const labelParts = [
                          lote.codigo_numerico ?? lote.codigo ?? `Lote ${lote.lote_id}`,
                          lote.armazem_nome ? ` - ${lote.armazem_nome}` : '',
                        ].join('');
                        const disponivelText =
                          lote.quantidade_disponivel !== undefined
                            ? ` (${lote.quantidade_disponivel} disponível)`
                            : '';
                        return (
                          <option key={lote.lote_id} value={lote.lote_id}>
                            {labelParts}
                            {disponivelText}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </div>

                  <div className="mb-3">
                    <Form.Label>Quantidade *</Form.Label>
                    <Form.Control
                      type="number"
                      required
                      min="1"
                      value={formData.quantidade}
                      onChange={(e) => setFormData((prev) => ({ ...prev, quantidade: Number(e.target.value) }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Form.Label>Unidade *</Form.Label>
                    <Form.Select
                      value={formData.unidade_id}
                      onChange={(e) => {
                        const value = e.target.value;
                        const option =
                          unidadesMedida.find((u) => String(u.id) === value) ||
                          unidadesMedida.find((u) => u.codigo === value) ||
                          UNIDADES_PADRAO.find((u) => u.codigo === value);
                        setFormData((prev) => ({
                          ...prev,
                          unidade: option?.codigo || prev.unidade,
                          unidade_id: value,
                        }));
                      }}
                    >
                      {unidadesMedida.length === 0 && <option value="">Selecione uma unidade</option>}
                      {unidadesMedida.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.codigo} - {u.descricao}
                        </option>
                      ))}
                      {unidadesMedida.length === 0 &&
                        UNIDADES_PADRAO.map((u) => (
                          <option key={u.codigo} value={u.codigo}>
                            {u.codigo} - {u.descricao}
                          </option>
                        ))}
                    </Form.Select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <Form.Label>Número do Pallet Inicial</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.numero_pallet}
                      onChange={(e) => setFormData((prev) => ({ ...prev, numero_pallet: Number(e.target.value) }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Form.Label>Código Tetra</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.codigo_tetra}
                      onChange={(e) => setFormData((prev) => ({ ...prev, codigo_tetra: e.target.value }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Form.Label>Código do Fornecedor</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.codigo_fornecedor}
                      onChange={(e) => setFormData((prev) => ({ ...prev, codigo_fornecedor: e.target.value }))}
                    />
                  </div>

                  <div className="mb-3">
                    <Form.Label>Data</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.data}
                      onChange={(e) => setFormData((prev) => ({ ...prev, data: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2 mt-4">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={loading || previewLoading}
                  onClick={handleVisualizar}
                >
                  {previewLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Gerando...
                    </>
                  ) : (
                    'Visualizar'
                  )}
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/admin/producao/etiquetas')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
              </div>
            </Form>

            {modeloSelecionado && (
              <div className="mt-4">
                <hr />
                <h5 className="mb-3">Preview da Etiqueta</h5>

                {previewData ? (
                  <div className="d-flex justify-content-center position-relative">
                    <EtiquetaPreview ref={previewRef} dados={previewData} modelo={modeloSelecionado} />
                    {!previewReady && (
                      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Gerando preview...
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border rounded bg-light p-4 text-center text-muted">
                    Clique em Visualizar para atualizar o preview com os dados atuais.
                  </div>
                )}
              </div>
            )}

            {!modeloSelecionado && (
              <div className="mt-4 p-4 text-center text-muted border rounded bg-light">
                <p className="mb-0">
                  <i>Selecione um modelo de etiqueta para visualizar o preview</i>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
